import { BlockStyleType } from "@pathwright/blocks-engine"
import { IconName } from "@pathwright/pathicons"
import { mediumGray, offBlack, offWhite, white } from "./ui"

export enum StyleMenuTypes {
  BLOCK = "block"
}

export type StyleHandler = (
  option: string,
  menuOption: StyleMenuTypes,
  styleOption: string
) => BlockStyleType | Error

export type StyleToolHandler = (option: string) => BlockStyleType

export type StyleToolOption = {
  label: string
  key: string
  type: string
  options: string[]
  handler: StyleToolHandler
  displayStyles?: boolean
  icons?: {
    [key: string]: IconName
  }
}

export type StyleMenuOption = {
  label: string
  tools: StyleToolOption[]
}

export type StyleMenuOptions = {
  [key in StyleMenuTypes]: StyleMenuOption
}

export type ColorStyle = {
  color: string
  backgroundColor: string
}

type WidthStyle = {
  paddingLeft: string
  paddingRight: string
}

type HeightStyle = {
  paddingTop: string
  paddingBottom: string
}

enum ColorOptions {
  LIGHTEST = "lightest",
  LIGHT = "light",
  DARKER = "darker",
  DARKEST = "darkest",
  CUSTOM = "custom"
}

enum WidthOptions {
  HORSPACINGFULL = "horspacingfull",
  HORSPACINGNORMAL = "horspacingnormal",
  HORSPACINGWIDE = "horspacingwide"
}

enum HeightOptions {
  VERTSPACINGFULL = "vertspacingfull",
  VERTSPACINGNORMAL = "vertspacingnormal",
  VERTSPACINGWIDE = "vertspacingwide"
}

export type BlockColorOptions = {
  [key in ColorOptions]: ColorStyle
}

export type BlockWidthOptions = {
  [key in WidthOptions]: WidthStyle
}

export type BlockHeightOptions = {
  [key in HeightOptions]: HeightStyle
}

type BlockStyleGroup = {
  color: BlockColorOptions
  width: BlockWidthOptions
  height: BlockHeightOptions
}

export type DefaultStyleOptions = {
  handler: StyleHandler
  block: BlockStyleGroup
}

// Type guard to check if a string is a valid style option key
const isStyleOption = (key: string): key is keyof BlockStyleGroup => {
  return key === "color" || key === "width" || key === "height"
}

// These are the baseline style options available by default
//  in the Block Styles menu;
//  some can be customized, ex: color
export const defaultStyleOptions: DefaultStyleOptions = {
  handler: (
    option: string,
    menuOption: StyleMenuTypes,
    styleOption: string
  ): BlockStyleType | Error => {
    if (!option || !menuOption || !styleOption)
      return new Error(
        "Can't apply block style — it looks like some information is missing."
      )

    // Currently only BLOCK menu type is supported
    if (menuOption !== StyleMenuTypes.BLOCK) {
      return new Error(`Unsupported menu option: ${menuOption}`)
    }

    // Validate and narrow the styleOption type
    if (!isStyleOption(styleOption)) {
      return new Error(`Unknown style option: ${styleOption}`)
    }

    // Access the style group (color, width, or height)
    const styleGroup = defaultStyleOptions.block[styleOption]

    // Check if the option exists in the style group
    if (!(option in styleGroup)) {
      return new Error(`Unknown option "${option}" for style "${styleOption}"`)
    }

    // Return the style object
    return styleGroup[option as keyof typeof styleGroup] as BlockStyleType
  },
  block: {
    color: {
      lightest: {
        color: offBlack,
        backgroundColor: white
      },
      light: {
        color: offBlack,
        backgroundColor: offWhite
      },
      darker: {
        color: white,
        backgroundColor: mediumGray
      },
      darkest: {
        color: white,
        backgroundColor: offBlack
      },
      // This is a placeholder to populate the custom menu option
      custom: {
        color: "",
        backgroundColor: ""
      }
    },
    width: {
      horspacingfull: {
        paddingLeft: "0",
        paddingRight: "0"
      },
      // horspacingtight: {
      //   paddingLeft: "30px",
      //   paddingRight: "30px"
      // },
      horspacingnormal: {
        paddingLeft: "40px",
        paddingRight: "40px"
      },
      horspacingwide: {
        paddingLeft: "80px",
        paddingRight: "80px"
      }
    },
    height: {
      vertspacingfull: {
        paddingTop: "0",
        paddingBottom: "0"
      },
      // vertspacingtight: {
      //   paddingTop: "30px",
      //   paddingBottom: "30px"
      // },
      vertspacingnormal: {
        paddingTop: "35px",
        paddingBottom: "35px"
      },
      vertspacingwide: {
        paddingTop: "60px",
        paddingBottom: "60px"
      }
    }
  }
}

// These are used by the Block Styles Menu to offer styling choices
export const blockStyleMenuOptions: StyleMenuOptions = {
  // Each key at this level represents a menu item (block, text, animation)
  block: {
    label: "Block",
    tools: [
      // Each of these represents a tool under a menu item (ex: block > color)
      {
        label: "Color",
        key: "blockColor",
        type: "color",
        displayStyles: true,
        options: Object.keys(defaultStyleOptions.block.color),
        handler: (option) => {
          const result = defaultStyleOptions.handler(
            option,
            StyleMenuTypes.BLOCK,
            "color"
          )
          if (result instanceof Error) {
            throw result
          }
          return result
        }
      },
      {
        label: "Horizontal Space",
        key: "blockWidth",
        type: "width",
        icons: {
          horspacingfull: "spacing-horizontal-small",
          // horspacingtight: "spacing-horizontal-small",
          horspacingnormal: "spacing-horizontal-medium",
          horspacingwide: "spacing-horizontal-large"
        },
        options: Object.keys(defaultStyleOptions.block.width),
        handler: (option) => {
          const result = defaultStyleOptions.handler(
            option,
            StyleMenuTypes.BLOCK,
            "width"
          )
          if (result instanceof Error) {
            throw result
          }
          return result
        }
      },
      {
        label: "Vertical Space",
        key: "blockHeight",
        type: "height",
        icons: {
          vertspacingfull: "spacing-vertical-small",
          // vertspacingtight: "spacing-vertical-small",
          vertspacingnormal: "spacing-vertical-medium",
          vertspacingwide: "spacing-vertical-large"
        },
        options: Object.keys(defaultStyleOptions.block.height),
        handler: (option) => {
          const result = defaultStyleOptions.handler(
            option,
            StyleMenuTypes.BLOCK,
            "height"
          )
          if (result instanceof Error) {
            throw result
          }
          return result
        }
      }
    ]
  }
  // Possible tools for future
  // text: {...},
  // animation: {...},
}

// Use this as boilerplate for a block's `styles` attribute (gql)
export const defaultBlockStyles = {
  selectedSettings: {
    blockColor: "lightest",
    blockWidth: "horspacingfull",
    blockHeight: "vertspacingnormal"
  },
  // These are CSS styles
  styleAttributes: {
    ...defaultStyleOptions.block.color.lightest,
    ...defaultStyleOptions.block.width.horspacingfull,
    ...defaultStyleOptions.block.height.vertspacingnormal
  }
} as BlockStyleType

export const defaultBlockLayoutStyles = {
  button: {
    selectedSettings: {
      ...defaultBlockStyles.selectedSettings,
      blockHeight: "vertspacingfull"
    },
    styleAttributes: {
      ...defaultBlockStyles.styleAttributes,
      ...defaultStyleOptions.block.height.vertspacingfull
    }
  }
}
